import React from 'react';
import { motion } from 'framer-motion';
import Slider from 'react-slick';
import { ChevronLeft, ChevronRight, Star, Quote } from 'lucide-react';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

const Testimonials = () => {
  const testimonials = [
    {
      name: 'Rajesh Kumar',
      role: 'Regular Player',
      content: 'SMR Online provides the fastest results I have ever seen. The app is very easy to use and withdrawals are instant. Highly recommended!',
      rating: 5,
      avatar: '👨'
    },
    {
      name: 'Amit Sharma',
      role: 'Premium Member',
      content: 'Best market rates in the industry. I have been using SMR Online for 6 months now and the experience has been amazing. Great support team!',
      rating: 5,
      avatar: '👨‍💼'
    },
    {
      name: 'Suresh Patel',
      role: 'Active User',
      content: 'Instant withdrawals are a game changer. I received my winnings within minutes. The app interface is clean and user-friendly.',
      rating: 5,
      avatar: '🧑'
    },
    {
      name: 'Vikram Singh',
      role: 'New User',
      content: 'Just started using SMR Online and already impressed with the speed and accuracy of results. The customer support is excellent!',
      rating: 4,
      avatar: '👨‍🦱'
    },
    {
      name: 'Deepak Verma',
      role: 'Daily Player',
      content: 'The best Matka app available. Fast, reliable, and trustworthy. I play daily and never faced any issues. Keep up the good work!',
      rating: 5,
      avatar: '🧔'
    }
  ];

  const CustomArrow = ({ direction, onClick }) => (
    <button
      onClick={onClick}
      className={`absolute top-1/2 -translate-y-1/2 z-10 w-10 h-10 md:w-12 md:h-12 bg-white hover:bg-green-50 border border-green-200 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-110 shadow-md ${direction === 'left' ? '-left-4 md:-left-6' : '-right-4 md:-right-6'
        }`}
    >
      {direction === 'left' ? (
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6 text-primary" />
      ) : (
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6 text-primary" />
      )}
    </button>
  );

  const settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    prevArrow: <CustomArrow direction="left" />,
    nextArrow: <CustomArrow direction="right" />,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
          arrows: false
        }
      }
    ]
  };

  return (
    <section className="py-16 md:py-20 lg:py-24 bg-green-50 relative overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0">
        <div className="absolute top-20 left-1/4 w-64 h-64 bg-primary/10 rounded-full blur-3xl animate-float"></div>
        <div className="absolute bottom-20 right-1/4 w-72 h-72 bg-accent/10 rounded-full blur-3xl animate-float" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="w-full max-w-[85%] mx-auto px-4 md:px-6 relative z-10">
        {/* Section Header */}
        <motion.div
          className="text-center mb-12 md:mb-16"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
        >
          <motion.span
            className="inline-block px-4 py-2 bg-white border border-green-200 rounded-full text-primary text-sm font-medium mb-4 shadow-sm"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Testimonials
          </motion.span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl xl:text-5xl font-bold mb-4 md:mb-6 text-gray-900">
            What Our <span className="text-gradient">Users Say</span>
          </h2>
          <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
            Join thousands of satisfied users who trust SMR Online for their Matka experience
          </p>
        </motion.div>

        {/* Testimonials Slider */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          viewport={{ once: true }}
          className="relative px-4 md:px-8"
        >
          <Slider {...settings}>
            {testimonials.map((testimonial, index) => (
              <div key={index} className="px-2 md:px-3">
                <div className="bg-white rounded-2xl p-6 md:p-8 h-full shadow-lg border border-gray-100 card-hover">
                  {/* Quote Icon */}
                  <div className="mb-4">
                    <Quote className="w-8 h-8 text-primary/30" />
                  </div>

                  {/* Content */}
                  <p className="text-gray-600 text-sm md:text-base leading-relaxed mb-6">
                    "{testimonial.content}"
                  </p>

                  {/* Rating */}
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star
                        key={i}
                        className={`w-4 h-4 ${i < testimonial.rating
                            ? 'text-yellow-500 fill-yellow-500'
                            : 'text-gray-300'
                          }`}
                      />
                    ))}
                  </div>

                  {/* User Info */}
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center text-2xl">
                      {testimonial.avatar}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-500">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </motion.div>
      </div>
    </section>
  );
};

export default Testimonials;
